// src/components/Navigation/ReportsDropdown.js
import React, { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Collapse, List, Typography, Divider
} from '@mui/material';
import {
  Assessment as ReportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  Analytics as AnalyticsIcon,
  TableChart as TableChartIcon,
  List as ListIcon,
  MedicalServices as CapsuleIcon
} from '@mui/icons-material';

const REPORT_COLOR = '#ff5722';

const ReportsDropdown = React.memo(({ isMobile, onNavigate }) => {

  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const reportItems = useMemo(() => [
    {
      text: 'Produção por Setor',
      path: '/relatorio-producao-setor',
      icon: <BarChartIcon fontSize="small" />,
    },
    {
      text: 'Encapsulação',
      path: '/relatorio-volume-producao',
      icon: <TrendingUpIcon fontSize="small" />,
    },
    {
      text: 'Encapsulação Detalhado',
      path: '/relatorio-encapsulacao-detalhado',
      icon: <ListIcon fontSize="small" />,
    },
    {
      text: 'Performance Produtiva',
      path: '/relatorio-performance',
      icon: <AnalyticsIcon fontSize="small" />,
    },
    {
      text: 'Relatório Tabular',
      path: '/relatorio-tabular',
      icon: <TableChartIcon fontSize="small" />,
    },
    {
      text: 'Produção de Cápsulas',
      path: '/relatorio-producao-capsulas',
      icon: <CapsuleIcon fontSize="small" />,
    }
  ], []);

  const isAnyReportActive = reportItems.some(item => location.pathname.startsWith(item.path));

  const handleClick = (event) => {
    if (isMobile) {
      setIsOpen(!isOpen);
    } else {
      if (!anchorEl) {
        setAnchorEl(event.currentTarget);
      }
    }
  };

  const handleMouseEnter = (event) => {
    if (isDesktop) {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
      const button = event.currentTarget.querySelector('#reports-menu-button') || event.currentTarget;
      setAnchorEl(button);
    }
  };

  const handleMouseLeave = () => {
    if (isDesktop) {
      const timeout = setTimeout(() => setAnchorEl(null), 200);
      setHoverTimeout(timeout);
    }
  };

  const handleMenuMouseEnter = () => {
    if (isDesktop && hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handleMenuMouseLeave = () => {
    if (isDesktop) {
      const timeout = setTimeout(() => setAnchorEl(null), 150);
      setHoverTimeout(timeout);
    }
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout]);

  const handleNavigate = useCallback((path) => {
    startTransition(() => {
      setAnchorEl(null);
      navigate(path);
    });
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (onNavigate) onNavigate();
  }, [navigate, onNavigate, hoverTimeout]);

  useEffect(() => {
    return () => {
      if (hoverTimeout) clearTimeout(hoverTimeout);
    };
  }, [hoverTimeout]);

  // Mobile - menu colapsável inline
  if (!isDesktop) {
    return (
      <>
        <ListItem disablePadding>
          <ListItemButton
            selected={isAnyReportActive}
            onClick={handleClick}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              mx: 1,
              '&.Mui-selected': {
                bgcolor: `${REPORT_COLOR}1A`,
                '&:hover': { bgcolor: `${REPORT_COLOR}2A` },
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                  color: REPORT_COLOR,
                  fontWeight: 'bold'
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: isAnyReportActive ? REPORT_COLOR : 'inherit' }}>
              <ReportIcon />
            </ListItemIcon>
            <ListItemText
              primary="Relatórios"
              primaryTypographyProps={{ fontWeight: isAnyReportActive ? 'bold' : 'normal' }}
            />
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ ml: 2, mr: 1, mb: 0.5 }}>
            {reportItems.map((item) => {
              const isSelected = location.pathname.startsWith(item.path);
              return (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      borderRadius: 1.5,
                      mb: 0.3,
                      py: 0.6,
                      pl: 3,
                      '&.Mui-selected': {
                        bgcolor: `${REPORT_COLOR}14`,
                        '&:hover': { bgcolor: `${REPORT_COLOR}22` },
                        '& .MuiListItemIcon-root': { color: REPORT_COLOR },
                        '& .MuiListItemText-primary': { color: REPORT_COLOR, fontWeight: 600 }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: isSelected ? REPORT_COLOR : 'text.secondary', minWidth: 30 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isSelected ? 600 : 'normal',
                        fontSize: '0.84rem'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  // Desktop - menu flutuante
  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ position: 'relative' }}
    >
      <ListItem disablePadding>
        <ListItemButton
          id="reports-menu-button"
          selected={isAnyReportActive}
          onClick={handleClick}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            mx: 1,
            '&.Mui-selected': {
              bgcolor: `${REPORT_COLOR}1A`,
              '&:hover': { bgcolor: `${REPORT_COLOR}2A` },
              '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                color: REPORT_COLOR,
                fontWeight: 'bold'
              }
            }
          }}
        >
          <ListItemIcon sx={{ color: isAnyReportActive ? REPORT_COLOR : 'inherit' }}>
            <ReportIcon />
          </ListItemIcon>
          <ListItemText
            primary="Relatórios"
            primaryTypographyProps={{ fontWeight: isAnyReportActive ? 'bold' : 'normal' }}
          />
          <ExpandMoreIcon sx={{
            fontSize: 20,
            color: 'text.secondary',
            transition: 'transform 0.2s',
            transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'none'
          }} />
        </ListItemButton>
      </ListItem>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 12,
          onMouseEnter: handleMenuMouseEnter,
          onMouseLeave: handleMenuMouseLeave,
          sx: {
            minWidth: 280,
            maxWidth: 320,
            ml: 1.5,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
        MenuListProps={{
          'aria-labelledby': 'reports-menu-button',
          disablePadding: true
        }}
      >
        {/* Header */}
        <Box sx={{
          px: 2.5,
          py: 1.5,
          background: `linear-gradient(135deg, ${REPORT_COLOR}12, ${REPORT_COLOR}08)`,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            Relatórios
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Visualize dados de produção
          </Typography>
        </Box>

        {/* Report items */}
        <Box sx={{ py: 0.5 }}>
          {reportItems.map((item, index) => {
            const isSelected = location.pathname.startsWith(item.path);
            return (
              <MenuItem
                key={item.text}
                onClick={() => handleNavigate(item.path)}
                selected={isSelected}
                sx={{
                  mx: 0.8,
                  my: 0.3,
                  borderRadius: 1.5,
                  py: 1.2,
                  px: 2,
                  gap: 1.5,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: `${REPORT_COLOR}0A`,
                  },
                  '&.Mui-selected': {
                    bgcolor: `${REPORT_COLOR}14`,
                    '&:hover': { bgcolor: `${REPORT_COLOR}1E` }
                  }
                }}
              >
                <ListItemIcon sx={{
                  color: isSelected ? REPORT_COLOR : 'text.secondary',
                  minWidth: 32,
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.875rem',
                    color: isSelected ? REPORT_COLOR : 'text.primary'
                  }}
                />
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </Box>
  );
});

export default ReportsDropdown;
